import React, { useState, useImperativeHandle, useRef, forwardRef, useEffect } from "react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";

import { AddCardInput } from "../CreditCardForm/CreditCardForm";
import { TextField, Label } from "@magic-engineering/components";
import "./PaymentDetails.scss";

import { isValidEmail } from "../../libs/helpers";
import api from "../../libs/api";
import isEqual from "lodash/isEqual";

const errorMessages = {
  invalid_cc_email: {
    title: "Invalid Email",
    text: "Please enter a valid email. Use comma to separate multiple values.",
  },
};

const PaymentDetails = (client, ref) => {
  const addCardInputRef = useRef(null);
  const [emailCC, setEmailCC] = useState(client?.billingEmailCCs);
  const [emailCCValid, setEmailCCValid] = useState(true);

  useEffect(() => {
    if (client?.billingEmailCCs && !emailCC) setEmailCC(client?.billingEmailCCs);
  }, [client, emailCC, setEmailCC]);

  useEffect(() => {
    if (emailCC) {
      const valid = emailCC.every((email) => isValidEmail(email) === true);
      setEmailCCValid(valid);
    }
  }, [emailCC, setEmailCCValid]);

  const submitEmailCC = async () => {
    try {
      if (!emailCCValid) return toast.error("Please enter a valid CC email!");
      if (isEqual(client?.billingEmailCCs, emailCC)) return;

      await api.post("/clients/update", {
        billing_email_cc: emailCC,
      });
      toast.success("Email saved.");
    } catch (e) {
      toast.error("Failed to save email address!");

      Sentry.withScope((scope) => {
        scope.setLevel("warning");
        Sentry.captureMessage(e);
      });
    }
  };

  useImperativeHandle(ref, () => ({
    submitEmailCC,
    addCardInputRef,
  }));

  return (
    <div className="payment-details" data-testid="payment-details">
      <div className="row p-4">
        <div className="col-12 col-lg-6 mb-3">
          {/* TODO: use type here instead of hex value */}
          <Label color="#8888B5" required={true}>
            Card Number
          </Label>
          <AddCardInput ref={addCardInputRef} />
        </div>
        <div className="col-12 col-lg-6 mb-3">
          {/* TODO: use type here instead of hex value */}
          <Label color="#8888B5">CC: Send a copy of invoices</Label>
          <TextField
            className="mb-0"
            name="billing_email_cc"
            placeholder="accounting@mycompany.com"
            onChange={(value) => {
              const inputs = value
                .split(",")
                .map((input) => input.trim().toLowerCase())
                .filter((value) => !!value);
              setEmailCC(inputs);
            }}
            dataTestId="email-input"
            defaultValue={emailCC}
            errors={
              emailCCValid
                ? {}
                : { billing_email_cc: { type: "validate", message: "invalid_cc_email" } }
            }
            errorMessages={errorMessages}
          />
        </div>
      </div>
    </div>
  );
};

const forwardedPaymentDetails = forwardRef(PaymentDetails);

export { forwardedPaymentDetails as default };
